import { useEffect } from 'react';
import { useRouteMatch, useLocation, useHistory } from 'react-router';

import { usePromotion } from './context';
import {
  PATH_COUPON_CODE_MATCH,
  USER_REFERRER_QUERY_PARAM,
  COUPON_CODE_QUERY_PARAM,
  COUPON_COOKIE_VERSION,
} from './constants';
import { ReferrerType } from './types';
import { isValidCouponMatch } from './utils/couponMatch';
import { sanitizeCouponCode } from './utils/sanitize';
import * as couponCookie from './utils/couponCookie';

interface UseSetPromotionOptions {
  // When true, suppresses all `couponCookie` mutations. This is likely only used during shadowing phase.
  skipCookieWrites?: boolean;
}

// Single init path for PromotionContext: URL path > URL query > cookie > nothing.
export function useSetPromotion({
  skipCookieWrites = false,
}: UseSetPromotionOptions = {}): void {
  const match = useRouteMatch(PATH_COUPON_CODE_MATCH);
  const location = useLocation();
  const history = useHistory();
  const { setPromotion, clearPromotion } = usePromotion();

  // URL path capture: /p/:couponCode
  useEffect(() => {
    if (!isValidCouponMatch(match?.params)) {
      return;
    }

    const code = sanitizeCouponCode(match?.params.couponCode);

    if (!code) {
      return;
    }

    if (!skipCookieWrites) {
      couponCookie.set({
        referrerAssociatedAt: new Date(),
        referrerCode: code,
        referrerType: ReferrerType.Partner,
        version: COUPON_COOKIE_VERSION,
      });

      history.replace({
        ...location,
        pathname: '/',
      });
    }

    void setPromotion({
      code,
      referrerType: ReferrerType.Partner,
      skipCookieWrites,
    });
  }, [match, history, location, setPromotion, skipCookieWrites]);

  // URL query capture: ?c= or ?ref=
  useEffect(() => {
    // Skip if the path matched already
    if (isValidCouponMatch(match?.params)) {
      return;
    }

    const query = new URLSearchParams(location.search);
    const couponCode = query.get(COUPON_CODE_QUERY_PARAM);
    const referrer = query.get(USER_REFERRER_QUERY_PARAM);

    if (couponCode) {
      const code = sanitizeCouponCode(couponCode);

      if (!code) {
        return;
      }

      if (!skipCookieWrites) {
        couponCookie.set({
          referrerAssociatedAt: new Date(),
          referrerCode: code,
          referrerType: ReferrerType.Partner,
          version: COUPON_COOKIE_VERSION,
        });

        query.delete(COUPON_CODE_QUERY_PARAM);

        history.replace({
          ...location,
          search: query.toString(),
        });
      }

      void setPromotion({
        code,
        referrerType: ReferrerType.Partner,
        skipCookieWrites,
      });
      return;
    }

    if (referrer) {
      const code = sanitizeCouponCode(referrer);

      if (!code) {
        return;
      }

      if (!skipCookieWrites) {
        couponCookie.set({
          referrerAssociatedAt: new Date(),
          referrerCode: code,
          referrerType: ReferrerType.User,
          version: COUPON_COOKIE_VERSION,
        });

        query.delete(USER_REFERRER_QUERY_PARAM);

        history.replace({
          ...location,
          search: query.toString(),
        });
      }

      void setPromotion({
        code,
        referrerType: ReferrerType.User,
        skipCookieWrites,
      });
      return;
    }
  }, [history, location, setPromotion, match, skipCookieWrites]);

  // Cookie fallback. Mount only, deps intentionally omitted.
  useEffect(() => {
    if (isValidCouponMatch(match?.params)) {
      return;
    }

    const query = new URLSearchParams(location.search);

    if (
      query.get(COUPON_CODE_QUERY_PARAM) ||
      query.get(USER_REFERRER_QUERY_PARAM)
    ) {
      return;
    }

    const cookie = couponCookie.get();

    if (cookie.referrerCode && cookie.referrerType) {
      void setPromotion({
        code: cookie.referrerCode,
        referrerType: cookie.referrerType,
        associatedAt: cookie.referrerAssociatedAt,
        skipCookieWrites,
      });
      return;
    }

    clearPromotion(skipCookieWrites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
